import { cn } from '@/lib/utils';
import { MessageContext } from 'contexts/MessageContext';
import { memo, useContext, useMemo, useRef } from 'react';

import {
  type IAction,
  type IMessageElement,
  type IStep
} from '@chainlit/react-client';

import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import { Messages } from '..';
import { AskActionButtons } from './AskActionButtons';
import { AskFileButton } from './AskFileButton';
import { MessageAvatar } from './Avatar';
import { MessageButtons } from './Buttons';
import { MessageContent } from './Content';
import Step from './Step';
import UserMessage from './UserMessage';

interface Props {
  message: IStep;
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  isScorable?: boolean;
  scorableRun?: IStep;
}

const EMPTY_ELEMENTS: IMessageElement[] = [];

const Message = memo(
  ({
    message,
    elements,
    actions,
    isRunning,
    indent,
    isScorable,
    scorableRun
  }: Props) => {
    const { allowHtml, cot, latex, onError } = useContext(MessageContext);
    const layoutMaxWidth = useLayoutMaxWidth();
    const contentRef = useRef<HTMLDivElement>(null);
    const isUserMessage = message.type === 'user_message';
    const isStep = !message.type.includes('message');
    // Respect Chainlit's configured chain-of-thought visibility while keeping
    // user and assistant messages on the standard rendering path.
    const toolCallSkip =
      isStep && cot === 'tool_call' && message.type !== 'tool';
    const hiddenSkip = isStep && cot === 'hidden';
    const skip = toolCallSkip || hiddenSkip;
    const showInputSection = Boolean(message.input && message.showInput);
    const shouldRenderOutput = !showInputSection || Boolean(message.output);

    const userMessageContent = useMemo(
      () => (
        <MessageContent
          elements={EMPTY_ELEMENTS}
          message={message}
          allowHtml={allowHtml}
          latex={latex}
        />
      ),
      [message, allowHtml, latex]
    );

    if (skip) {
      if (!message.steps) {
        return null;
      }
      return (
        <Messages
          messages={message.steps}
          elements={elements}
          actions={actions}
          indent={indent}
          isRunning={isRunning}
          scorableRun={scorableRun}
        />
      );
    }

    return (
      <>
        <div data-step-type={message.type} className="step py-2.5">
          <div
            className="flex flex-col"
            style={{
              maxWidth: indent ? '100%' : layoutMaxWidth
            }}
          >
            <div
              className={cn('flex flex-grow pb-1')}
              id={`step-${message.id}`}
            >
              {/* User message is displayed differently */}
              {isUserMessage ? (
                <div className="flex flex-col flex-grow max-w-full">
                  <UserMessage message={message} elements={elements}>
                    {userMessageContent}
                  </UserMessage>
                </div>
              ) : (
                <div className="ai-message group flex w-full gap-2.5">
                  {(!isStep || !indent) && message.type !== 'tool' ? (
                    <MessageAvatar
                      author={message.metadata?.avatarName || message.name}
                      className={isStep ? 'mt-0.5' : 'mt-3'}
                      isError={message.isError}
                    />
                  ) : null}
                  {/* Display the step and its children */}
                  {isStep ? (
                    <Step step={message} isRunning={isRunning}>
                      {showInputSection ? (
                        <MessageContent
                          elements={elements}
                          message={message}
                          allowHtml={allowHtml}
                          latex={latex}
                          sections={['input']}
                        />
                      ) : null}
                      {message.steps ? (
                        <Messages
                          messages={message.steps.filter(
                            (s) => !s.type.includes('message')
                          )}
                          elements={elements}
                          actions={actions}
                          indent={indent + 1}
                          isRunning={isRunning}
                        />
                      ) : null}
                      {shouldRenderOutput ? (
                        <MessageContent
                          ref={contentRef}
                          elements={elements}
                          message={message}
                          allowHtml={allowHtml}
                          latex={latex}
                          sections={showInputSection ? ['output'] : undefined}
                        />
                      ) : null}
                      <MessageButtons
                        message={message}
                        actions={actions}
                        contentRef={contentRef}
                      />
                    </Step>
                  ) : (
                    // Display an assistant message
                    <div className="assistant-message-surface flex min-w-0 flex-grow flex-col items-start gap-2 rounded-2xl border border-border/70 bg-card px-4 py-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                      <MessageContent
                        ref={contentRef}
                        elements={elements}
                        message={message}
                        allowHtml={allowHtml}
                        latex={latex}
                      />

                      <AskFileButton messageId={message.id} onError={onError} />
                      <AskActionButtons
                        actions={actions}
                        messageId={message.id}
                      />

                      <MessageButtons
                        message={message}
                        actions={actions}
                        run={
                          scorableRun && isScorable ? scorableRun : undefined
                        }
                        contentRef={contentRef}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Make sure the child assistant messages of a step are displayed at the root level. */}
        {message.steps && isStep ? (
          <Messages
            messages={message.steps.filter((s) => s.type.includes('message'))}
            elements={elements}
            actions={actions}
            indent={0}
            isRunning={isRunning}
            scorableRun={scorableRun}
          />
        ) : null}
        {/* Display the child steps if the message is not a step (usually a user message). */}
        {message.steps && !isStep ? (
          <Messages
            messages={message.steps}
            elements={elements}
            actions={actions}
            indent={indent}
            isRunning={isRunning}
          />
        ) : null}
      </>
    );
  }
);

export { Message };
